import { useState, useRef, useEffect } from 'react';

// ---------------------------------------------------------------------------
// Product demo video — placed on the landing page immediately after the hero.
//
// PERFORMANCE CONTRACT (this is the whole point of the component):
//   The video file is NEVER requested on page load. What renders at rest is a
//   click-to-play FACADE — a lazy-loaded poster <img> plus a play button. The
//   real <video> (a 12.6 MB MP4) is only mounted, and only begins downloading,
//   once the user clicks. So this section adds nothing to LCP, TBT, or transfer
//   size on first paint, and cannot move the Lighthouse score.
//
//   The section is ALSO code-split out of the landing entry chunk (see
//   Landing.tsx: it's a React.lazy import gated behind `belowReady`), so even
//   this facade's JS/CSS stays off the critical path.
//
// LAYOUT: the whole section sits on Parchment (light). The one dark→light break
//   happens crisply at the hero's own bottom edge — NOT with a second green zone
//   inside this section (that duplicated the hero's green and made the heading
//   read as the tail of the hero). The dark video poster then POPS on the light
//   ground instead of merging into green.
//
// Assets are self-hosted on the app's own CDN (entredash.com), content-addressed
// and cache-forever — no third-party embed (a screen.studio iframe would pull in
// their whole app, cookies, and analytics, tanking the score).
// ---------------------------------------------------------------------------

// The optimized MP4 (1080p, 30fps, faststart so playback starts immediately).
const VIDEO_SRC =
  'https://entredash.com/_/files/public/assets/6060f4fe1dafcb21e3ecc86fac832960a240a90df9f470d5dd62539ab9285602.mp4';

// Full-res poster master (1920×1080). Served resized + WebP via CDN params so
// the resting frame is a small, crisp image at the ~1040px display size (2× for
// Retina). It's below the fold and lazy, so it costs nothing on first paint.
const POSTER_MASTER =
  'https://entredash.com/_/files/public/assets/fc97e09d0652ea37c8b2eead58ccab550defbb48d95acc73801eb683f4f9a6a5.jpg';
const POSTER_IMG = `${POSTER_MASTER}?w=1080&dpr=2&fm=webp&q=82`;

export function DemoVideo() {
  const [playing, setPlaying] = useState(false);
  // Whether to autoplay on mount — false under prefers-reduced-motion, where we
  // mount the video paused with controls and let the user press play.
  const autoPlayRef = useRef(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  function handlePlay() {
    autoPlayRef.current = !(
      typeof window !== 'undefined' &&
      window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    );
    setPlaying(true);
  }

  // Once the <video> mounts (from the user's click), start it. The click is the
  // user gesture, so playback with sound is permitted. If the browser refuses,
  // the always-present controls let the user start it manually.
  useEffect(() => {
    if (playing && autoPlayRef.current) {
      videoRef.current?.play().catch(() => {
        /* controls are shown; user can start it manually */
      });
    }
  }, [playing]);

  return (
    <section className="demoband" aria-label="Product demo">
      <style>{DEMO_VIDEO_CSS}</style>

      {/* Eyebrow + heading sit on the light ground, above the frame, in normal
          reading order — so the section introduces itself before the media. */}
      <span className="demoband__eyebrow">
        <i />
        See it in motion
      </span>
      <h2 className="demoband__title">Watch how an idea gets worked on in EntreDash.</h2>

      {/* The dark frame floats on parchment. The facade <button> and the swapped
          <video> share the exact same box, so there is zero layout shift on play. */}
      {playing ? (
        <video
          ref={videoRef}
          className="demoframe demoframe--video"
          controls
          playsInline
          preload="auto"
          poster={POSTER_IMG}
          onEnded={() => setPlaying(false)}
        >
          <source src={VIDEO_SRC} type="video/mp4" />
        </video>
      ) : (
        <button
          type="button"
          className="demoframe"
          onClick={handlePlay}
          aria-label="Play the product demo — 57 seconds"
        >
          <img
            className="demoframe__poster"
            src={POSTER_IMG}
            width={1920}
            height={1080}
            loading="lazy"
            decoding="async"
            alt=""
          />
          <span className="demoframe__scrim" aria-hidden="true" />
          <span className="demoframe__play" aria-hidden="true">
            <span className="tri" />
          </span>
          <span className="demoframe__dur" aria-hidden="true">
            0:57
          </span>
        </button>
      )}
    </section>
  );
}

const DEMO_VIDEO_CSS = `
  /* The section IS the light band. The dark→light break happens at the hero's
     own bottom edge directly above — there must be no border, hairline, or gap
     between the hero and this section, or that one clean seam gets muddied. The
     inset top shadow reads as the hero casting a soft "lip" onto the parchment,
     making the crisp seam feel intentional (no colour, no line). */
  .demoband {
    background: #F4EFE2;
    box-shadow: inset 0 24px 40px -24px rgba(15,23,20,0.18);
    padding: 96px 32px 48px;
    text-align: center;
  }

  .demoband__eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    margin: 0 0 16px;
    font-family: var(--f-sans);
    font-weight: 500;
    font-size: 13px;
    line-height: 1;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #855f20;   /* deep brass — standard brass fails AA on parchment at this size */
  }
  .demoband__eyebrow i { width: 22px; height: 2px; background: #855f20; }

  .demoband__title {
    margin: 0;
    font-family: var(--f-serif);
    font-weight: 400;
    font-size: clamp(26px, 3vw, 34px);
    line-height: 1.1;
    letter-spacing: -0.015em;
    color: #0F1714;   /* full-strength ink — reads unmistakably as a new section */
  }

  /* The frame floats fully on light — no straddle, no negative margin. The
     hairline ring gives the dark poster a defined boundary so it reads as a
     framed screen, not a dark rectangle bleeding onto the cream. */
  .demoframe {
    display: block;
    position: relative;
    width: 100%;
    max-width: 1040px;
    margin: 48px auto 0;
    aspect-ratio: 16 / 9;
    border: 0;
    padding: 0;
    cursor: pointer;
    background: #12271d;      /* covers any sub-pixel gap; no white flash before the poster paints */
    border-radius: 12px;
    overflow: hidden;
    box-shadow:
      0 0 0 1px rgba(15,23,20,0.07),
      0 1px 2px rgba(15,23,20,0.05),
      0 10px 24px rgba(15,23,20,0.10),
      0 40px 80px -24px rgba(15,23,20,0.26);
    transition: box-shadow 120ms ease;
  }
  .demoframe--video { cursor: default; }
  .demoframe:hover {
    box-shadow:
      0 0 0 1px rgba(15,23,20,0.09),
      0 1px 2px rgba(15,23,20,0.05),
      0 12px 28px rgba(15,23,20,0.14),
      0 48px 96px -24px rgba(15,23,20,0.32);
  }
  .demoframe:focus-visible {
    outline: 2px solid color-mix(in oklch, var(--brass, #b5853c) 70%, white);
    outline-offset: 2px;
  }

  .demoframe__poster,
  .demoframe--video {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  /* The video element participates in flow (it IS the frame), so it isn't
     absolutely positioned like the poster — reset that. */
  .demoframe--video { position: relative; inset: auto; }

  /* Radial darkening under the disc so it always has contrast over the poster. */
  .demoframe__scrim {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: radial-gradient(160px 160px at 50% 50%, rgba(15,23,20,0.30), transparent 72%);
  }

  /* Play disc — parchment fill, brass invitation ring, heartwood triangle. It
     sits on the dark poster, so its values are unaffected by the light ground.
     On hover it fills Spark Bright (the "go watch it" moment). */
  .demoframe__play {
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: 76px; height: 76px;
    border-radius: 999px;
    background: #F4EFE2;
    display: grid;
    place-items: center;
    box-shadow: 0 0 0 1.5px #B5853C, 0 10px 30px rgba(15,23,20,0.30);
    transition: background-color 120ms ease, box-shadow 120ms ease;
  }
  .demoframe__play .tri {
    width: 0; height: 0;
    border-style: solid;
    border-width: 11px 0 11px 18px;
    border-color: transparent transparent transparent #1b3a2e;
    margin-left: 5px;   /* optical centering of the triangle in the disc */
    transition: border-left-color 120ms ease;
  }
  .demoframe:hover .demoframe__play {
    background: #4FD992;
    box-shadow: 0 0 0 1.5px #4FD992, 0 12px 34px rgba(15,23,20,0.34);
  }
  .demoframe:hover .demoframe__play .tri { border-left-color: #0F1714; }

  /* Runtime timecode — mono, the brand's "this is real, not marketing" texture.
     Also sits on the dark poster, so its values are unchanged. */
  .demoframe__dur {
    position: absolute;
    left: 14px; bottom: 14px;
    font-family: var(--f-mono);
    font-weight: 500;
    font-size: 12px;
    line-height: 1;
    letter-spacing: 0.02em;
    color: #F4EFE2;
    background: rgba(15,23,20,0.55);
    padding: 5px 8px;
    border-radius: 6px;
  }

  @media (max-width: 640px) {
    .demoband { padding: 64px 20px 40px; box-shadow: inset 0 16px 24px -16px rgba(15,23,20,0.16); }
    .demoband__eyebrow { font-size: 12px; letter-spacing: 0.12em; margin-bottom: 12px; }
    .demoband__title { font-size: clamp(22px, 6.4vw, 28px); }
    .demoframe { margin-top: 32px; }
    .demoframe__play {
      width: 56px; height: 56px;
      box-shadow: 0 0 0 1.25px #B5853C, 0 8px 22px rgba(15,23,20,0.30);
    }
    .demoframe__play .tri { border-width: 9px 0 9px 15px; margin-left: 4px; }
    .demoframe__scrim { background: radial-gradient(110px 110px at 50% 50%, rgba(15,23,20,0.32), transparent 72%); }
    .demoframe__dur { left: 10px; bottom: 10px; font-size: 11px; padding: 4px 7px; }
  }

  /* Motion rule: no transform-on-hover, no scroll-in animation. The frame lifts
     via shadow only; the disc's colour shift is the feedback. */
  @media (prefers-reduced-motion: reduce) {
    .demoframe, .demoframe__play, .demoframe__play .tri { transition: none; }
  }
`;
